import React, { useState, useEffect } from "react";
import { Calculator, X, AlertTriangle } from "lucide-react";
import { useDraftInvoice } from "../../hooks/useDraftInvoice";
import { useAccountBillability } from "../../hooks/useAccountBillability";
import { StochasticInvoiceLineItem } from "@/api/createDraftInvoice/types";
import { fetchCampaignProducts } from "@/api/fetchCampaignProducts/fetchCampaignProductsApi";
import { CampaignProduct } from "@/api/fetchCampaignProducts/types";

const SKU_MAPPINGS = {
  product: { PC: "SM018-01", LTR: "SM018-02" },
  postage: { PC: "SM019-01", LTR: "SM019-02" },
} as const;

const getProductSkuCode = (productCode: string): string =>
  SKU_MAPPINGS.product[productCode as keyof typeof SKU_MAPPINGS.product] ??
  "SM018-01";

const getPostageSkuCode = (productCode: string): string =>
  SKU_MAPPINGS.postage[productCode as keyof typeof SKU_MAPPINGS.postage] ??
  "SM019-01";

const DraftInvoiceCalculator: React.FC = () => {
  const { invoice, loading, error, createDraft, clearInvoice } =
    useDraftInvoice();

  const [accountId, setAccountId] = useState("PS12641");
  const [submittedAccountId, setSubmittedAccountId] = useState<string | null>(
    null,
  );
  const { isBillable, getBillabilityMessage } = useAccountBillability(
    submittedAccountId ? [submittedAccountId] : [],
  );
  const [invoiceDate, setInvoiceDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });
  const [dueDate, setDueDate] = useState(() => {
    const today = new Date();
    const dueDate = new Date(today);
    dueDate.setDate(today.getDate() + 7);
    return dueDate.toISOString().split("T")[0];
  });

  const [campaignProducts, setCampaignProducts] = useState<CampaignProduct[]>(
    [],
  );
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    null,
  );
  const [productQuantity, setProductQuantity] = useState(100);
  const [postageTotalAmount, setPostageTotalAmount] = useState(75.0);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const response = await fetchCampaignProducts();
        const products = Array.isArray(response.data.campaignProducts)
          ? response.data.campaignProducts
          : [];
        setCampaignProducts(products);
        if (products.length > 0) {
          setSelectedProductId(products[0].id);
        }
      } catch (err) {
        console.error("Failed to load campaign products:", err);
        setCampaignProducts([]);
      } finally {
        setLoadingProducts(false);
      }
    };
    loadProducts();
  }, []);

  const selectedProduct = Array.isArray(campaignProducts)
    ? campaignProducts.find((p) => p.id === selectedProductId)
    : undefined;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedProduct) {
      return;
    }

    const productSkuCode = getProductSkuCode(selectedProduct.code);
    const postageSkuCode = getPostageSkuCode(selectedProduct.code);

    const requestData = {
      accountId,
      productId: selectedProductId!,
      postageTotalAmount: Number(postageTotalAmount),
      ...(invoiceDate && { invoiceDate }),
      ...(dueDate && { dueDate }),
      lines: [
        {
          skuCode: productSkuCode,
          quantity: Number(productQuantity),
          unitPrice: 0,
        },
        {
          skuCode: postageSkuCode,
          quantity: Number(productQuantity),
          unitPrice: 0,
        },
      ],
    };

    setSubmittedAccountId(accountId);
    createDraft(requestData);
  };

  const handleClear = () => {
    clearInvoice();
    setSubmittedAccountId(null);
    if (campaignProducts.length > 0) {
      setSelectedProductId(campaignProducts[0].id);
    }
    setProductQuantity(100);
    setPostageTotalAmount(75.0);
  };

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === "string" ? parseFloat(amount) : amount;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(num);
  };

  const formatPercent = (rate: string | number) => {
    const num = typeof rate === "string" ? parseFloat(rate) : rate;
    return `${(num * 100).toFixed(2)}%`;
  };

  const formatDate = (dateString: string) => {
    const [year, month, day] = dateString.split("-");
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Calculator className="w-8 h-8" />
            Draft Invoice Calculator
          </h1>
          <p className="text-gray-600 mt-2">
            Test invoice calculations with arbitrary values. Draft invoices
            include accurate tax calculations but are not persisted to the
            database.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Form */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Invoice Details</h2>

            <form className="space-y-4" onSubmit={handleSubmit}>
              {/* Account Information */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account ID *
                </label>
                <input
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onChange={(e) => setAccountId(e.target.value)}
                  placeholder="e.g., PS12641"
                  required
                  type="text"
                  value={accountId}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Must exist in CARA database
                </p>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invoice Date
                  </label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onChange={(e) => setInvoiceDate(e.target.value)}
                    type="date"
                    value={invoiceDate}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Due Date
                  </label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onChange={(e) => setDueDate(e.target.value)}
                    type="date"
                    value={dueDate}
                  />
                </div>
              </div>

              {/* Campaign Product */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Campaign Product *
                </label>
                <div className="p-3 bg-gray-50 rounded-md space-y-2">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Product
                    </label>
                    <select
                      className="w-full px-2 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                      disabled={loadingProducts}
                      onChange={(e) =>
                        setSelectedProductId(Number(e.target.value))
                      }
                      required
                      value={selectedProductId || ""}
                    >
                      {loadingProducts ? (
                        <option value="">Loading products...</option>
                      ) : campaignProducts.length === 0 ? (
                        <option value="">No products available</option>
                      ) : (
                        campaignProducts.map((product) => (
                          <option key={product.id} value={product.id}>
                            {product.name}
                          </option>
                        ))
                      )}
                    </select>
                    {selectedProduct && (
                      <div className="text-xs mt-1 space-y-1">
                        <p className="text-gray-500">
                          {selectedProduct.description}
                        </p>
                        <div className="flex gap-4">
                          <p className="text-gray-700">
                            <span className="font-medium">
                              Target Audience:
                            </span>{" "}
                            <span className="capitalize">
                              {selectedProduct.targetAudience}
                            </span>
                          </p>
                          <p className="text-gray-700">
                            <span className="font-medium">Base Price:</span> $
                            {selectedProduct.targetAudience === "customer"
                              ? parseFloat(
                                  selectedProduct.customerPrice,
                                ).toFixed(4)
                              : parseFloat(
                                  selectedProduct.prospectPrice,
                                ).toFixed(4)}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Quantity
                    </label>
                    <input
                      className="w-full px-2 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      min="1"
                      onChange={(e) =>
                        setProductQuantity(Number(e.target.value))
                      }
                      required
                      step="1"
                      type="number"
                      value={productQuantity}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Unit price will be calculated based on product pricing and
                      payment terms
                    </p>
                  </div>
                </div>
              </div>

              {/* Postage */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Postage & Handling *
                </label>
                <div className="p-3 bg-gray-50 rounded-md space-y-2">
                  <div className="text-xs text-gray-600 mb-2">
                    <p>
                      SKU and quantity automatically determined by campaign
                      product
                    </p>
                    {selectedProduct && (
                      <p className="mt-1 font-medium">
                        {getPostageSkuCode(selectedProduct.code)} - Shipping and
                        Handling (
                        {selectedProduct.code === "PC" ? "Postcard" : "Letter"})
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Total Amount
                    </label>
                    <input
                      className="w-full px-2 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      min="0"
                      onChange={(e) =>
                        setPostageTotalAmount(Number(e.target.value))
                      }
                      required
                      step="0.01"
                      type="number"
                      value={postageTotalAmount}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Quantity: {productQuantity} (matches product quantity)
                    </p>
                  </div>
                </div>
              </div>

              {/* Error Display */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex gap-2">
                <button
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  disabled={loading}
                  type="submit"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Calculating...
                    </>
                  ) : (
                    <>
                      <Calculator className="w-4 h-4" />
                      Calculate Invoice
                    </>
                  )}
                </button>
                {invoice && (
                  <button
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2"
                    onClick={handleClear}
                    type="button"
                  >
                    <X className="w-4 h-4" />
                    Clear
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Results Display */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Calculated Invoice</h2>

            {!invoice && !loading && (
              <div className="text-center py-12 text-gray-500">
                <Calculator className="w-16 h-16 mx-auto mb-4 opacity-20" />
                <p>Enter invoice details and click Calculate to see results</p>
              </div>
            )}

            {loading && (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-4">Calculating taxes...</p>
              </div>
            )}

            {invoice && !loading && (
              <div className="space-y-6">
                {/* Draft Mode Notice */}
                <div className="flex items-start gap-3 p-3 rounded-md bg-amber-50 border border-amber-200">
                  <AlertTriangle
                    className="text-amber-600 mt-0.5 shrink-0"
                    size={16}
                  />
                  <p className="text-sm text-amber-800">
                    <strong>Draft Mode:</strong> This invoice was calculated but
                    not saved to the database
                  </p>
                </div>

                {/* Billability Warning */}
                {submittedAccountId &&
                  isBillable(submittedAccountId) === false && (
                    <div className="flex items-start gap-3 p-3 rounded-md bg-amber-50 border border-amber-200">
                      <AlertTriangle
                        className="text-amber-600 mt-0.5 shrink-0"
                        size={16}
                      />
                      <div>
                        <p className="text-sm font-medium text-amber-800">
                          This account cannot be billed
                        </p>
                        <p className="text-xs text-amber-700 mt-0.5">
                          {getBillabilityMessage(submittedAccountId!)}
                        </p>
                      </div>
                    </div>
                  )}

                {/* Invoice Header */}
                <div className="space-y-4">
                  {/* Bill From and Bill To */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Bill From */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                        Bill From
                      </h4>
                      <div className="text-sm">
                        {invoice.billFrom?.name && (
                          <p className="font-medium">{invoice.billFrom.name}</p>
                        )}
                        <p className="font-medium">
                          {invoice.billFrom?.company}
                        </p>
                        <p>{invoice.billFrom?.addressLine1}</p>
                        {invoice.billFrom?.addressLine2 && (
                          <p>{invoice.billFrom.addressLine2}</p>
                        )}
                        <p>
                          {invoice.billFrom?.city}, {invoice.billFrom?.state}{" "}
                          {invoice.billFrom?.postalCode}
                        </p>
                        <p className="mt-1 text-gray-600">
                          {invoice.billFrom?.phone}
                        </p>
                        <p className="text-gray-600">
                          {invoice.billFrom?.email}
                        </p>
                      </div>
                    </div>

                    {/* Bill To */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                        Bill To
                      </h4>
                      <div className="text-sm">
                        {invoice.billTo?.name && (
                          <p className="font-medium">{invoice.billTo.name}</p>
                        )}
                        <p className="font-medium">{invoice.billTo?.company}</p>
                        <p>{invoice.billTo?.addressLine1}</p>
                        {invoice.billTo?.addressLine2 && (
                          <p>{invoice.billTo.addressLine2}</p>
                        )}
                        <p>
                          {invoice.billTo?.city}, {invoice.billTo?.state}{" "}
                          {invoice.billTo?.postalCode}
                        </p>
                        <p className="mt-1 text-gray-600">
                          {invoice.billTo?.phone}
                        </p>
                        <p className="text-gray-600">{invoice.billTo?.email}</p>
                      </div>
                    </div>
                  </div>

                  {/* Invoice Info */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                      Invoice Details
                    </h4>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Invoice #:</span>
                        <span className="font-medium">
                          {invoice.invoiceNumber}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Job #:</span>
                        <span className="font-medium">{invoice.jobNumber}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Invoice Date:</span>
                        <span>{formatDate(invoice.invoiceDate)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Due Date:</span>
                        <span>{formatDate(invoice.dueDate)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Mail Date:</span>
                        <span>{formatDate(invoice.mailDate)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Terms:</span>
                        <span>{invoice.terms}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Ship To (if different from Bill To) */}
                {invoice.shipTo?.company !== invoice.billTo?.company && (
                  <div className="p-4 bg-gray-50 rounded">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                      Ship To
                    </h4>
                    <div className="text-sm">
                      {invoice.shipTo?.name && (
                        <p className="font-medium">{invoice.shipTo.name}</p>
                      )}
                      <p className="font-medium">{invoice.shipTo?.company}</p>
                      <p>{invoice.shipTo?.addressLine1}</p>
                      {invoice.shipTo?.addressLine2 && (
                        <p>{invoice.shipTo.addressLine2}</p>
                      )}
                      <p>
                        {invoice.shipTo?.city}, {invoice.shipTo?.state}{" "}
                        {invoice.shipTo?.postalCode}
                      </p>
                    </div>
                  </div>
                )}

                {/* Invoice Line Items */}
                <table className="w-full">
                  <thead className="border-b-2 border-gray-300">
                    <tr>
                      <th className="text-left py-3 text-sm font-semibold text-gray-700">
                        Description
                      </th>
                      <th className="text-right py-3 text-sm font-semibold text-gray-700">
                        Quantity
                      </th>
                      <th className="text-right py-3 text-sm font-semibold text-gray-700">
                        Unit Price
                      </th>
                      <th className="text-right py-3 text-sm font-semibold text-gray-700">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.lineItems?.map(
                      (item: StochasticInvoiceLineItem, idx: number) => (
                        <tr className="border-b" key={idx}>
                          <td className="py-3 text-sm text-gray-900">
                            {item.description}
                          </td>
                          <td className="py-3 text-sm text-right text-gray-700">
                            {item.quantity.toLocaleString()}
                          </td>
                          <td className="py-3 text-sm text-right text-gray-700">
                            ${item.unitPrice.toFixed(4)}
                          </td>
                          <td className="py-3 text-sm text-right font-medium text-gray-900">
                            ${item.total.toFixed(2)}
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>

                {/* Invoice Totals and Summary */}
                <div className="grid grid-cols-2 gap-6">
                  {/* Summary Stats */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">
                      Summary
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Pieces:</span>
                        <span className="font-medium">
                          {(invoice.totalPieces || 0).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Price per Piece:</span>
                        <span className="font-medium">
                          ${(invoice.pricePerPiece || 0).toFixed(4)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Totals */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">
                      Payment Details
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Subtotal:</span>
                        <span className="font-medium">
                          ${(invoice.subTotal || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tax:</span>
                        <span className="font-medium">
                          ${(invoice.tax || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between pt-2 mt-2 border-t border-gray-300">
                        <span className="font-semibold text-gray-900">
                          Total:
                        </span>
                        <span className="font-bold text-gray-900">
                          ${(invoice.total || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between pt-2 mt-2 border-t-2 border-gray-400">
                        <span className="text-base font-bold text-gray-900">
                          Balance Due:
                        </span>
                        <span className="text-base font-bold text-blue-600">
                          ${(invoice.balanceDue || 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DraftInvoiceCalculator;
